import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, Search, Package, Layers } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader } from './ui/card';
import CompositeProductForm from './CompositeProductForm';

const API = process.env.REACT_APP_BACKEND_URL;

// Kategori default
const DEFAULT_CATEGORIES = [
  'Makanan',
  'Minuman',
  'Snack',
  'Es Krim',
  'Kopi',
  'Teh',
  'Jus',
  'Lainnya'
];

export default function ProductManagement() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showCompositeForm, setShowCompositeForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedBranchFilter, setSelectedBranchFilter] = useState(''); // NEW: Branch filter
  const [selectedTypeFilter, setSelectedTypeFilter] = useState(''); // NEW: Type filter (simple/composite)
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  
  // Get current user from localStorage
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    price: '',
    category: '',
    stock: '',
    branch_id: '',
    image_url: '',
    manual_hpp: '',
    stock_change_reason: ''
  });
  
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [initialStock, setInitialStock] = useState(null);

  // Format number with thousand separator
  const formatNumber = (value) => {
    if (!value) return '';
    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  };

  // Remove format from number
  const unformatNumber = (value) => {
    return value.replace(/\./g, '');
  };

  useEffect(() => {
    fetchProducts();
    fetchCategories();
    fetchBranches();
  }, []);

  const fetchProducts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/products`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setProducts(response.data.products);
        console.log(`Loaded ${response.data.products.length} products`);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      // Don't show error toast if it's just a refresh
      if (loading) {
        toast.error('Gagal memuat produk');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/products/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setCategories(response.data.categories);
        console.log(`Loaded ${response.data.categories.length} categories`);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      // Silent fail for categories
    }
  };

  const fetchBranches = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/branches`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setBranches(response.data.branches);
        console.log(`Loaded ${response.data.branches.length} branches`);
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
      // Silent fail for branches
    }
  };

  const handleImageUpload = async (file) => {
    if (!file) return null;
    
    try {
      setUploadingImage(true);
      const formData = new FormData();
      formData.append('file', file);
      
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/api/products/upload-image`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data.success) {
        return response.data.image_url;
      }
      
      return null;
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Gagal upload gambar');
      return null;
    } finally {
      setUploadingImage(false);
    }
  };
  
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Ukuran file maksimal 5MB');
        return;
      }
      
      // Validate file type
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        toast.error('Format file harus JPEG, PNG, atau WebP');
        return;
      }
      
      setImageFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('token');
      const user = JSON.parse(localStorage.getItem('user'));
      
      // Check if stock has changed and reason is required
      if (editingProduct && initialStock !== null) {
        const newStock = parseInt(formData.stock) || 0;
        if (newStock !== initialStock && !formData.stock_change_reason.trim()) {
          toast.error('Alasan perubahan stok wajib diisi!', {
            description: 'Masukkan alasan kenapa stok diubah untuk keperluan audit',
            duration: 4000
          });
          return;
        }
      }
      
      // Remove format from price before sending
      const cleanPrice = unformatNumber(formData.price);
      
      if (!cleanPrice || isNaN(cleanPrice)) {
        toast.error('Harga tidak valid');
        return;
      }
      
      // Remove format from manual_hpp if provided
      const cleanManualHpp = formData.manual_hpp ? unformatNumber(formData.manual_hpp) : null;
      
      // Upload image first if there's a new image
      let imageUrl = formData.image_url; // Keep existing image URL
      if (imageFile) {
        const uploadedUrl = await handleImageUpload(imageFile);
        if (uploadedUrl) {
          imageUrl = uploadedUrl;
        }
      }
      
      const productData = {
        name: formData.name,
        price: parseFloat(cleanPrice),
        category: formData.category,
        stock: parseInt(formData.stock) || 0,
        branch_id: formData.branch_id || user.branch_id,
        image_url: imageUrl,
        manual_hpp: cleanManualHpp ? parseFloat(cleanManualHpp) : null
      };
      
      // Add stock_change_reason if stock changed
      if (editingProduct && initialStock !== null && productData.stock !== initialStock) {
        productData.stock_change_reason = formData.stock_change_reason;
      }

      console.log('Submitting product:', productData);

      let response;
      let success = false;
      
      try {
        if (editingProduct) {
          // Update
          response = await axios.put(`${API}/api/products/${editingProduct.id}`, productData, {
            headers: { Authorization: `Bearer ${token}` }
          });
          console.log('Update response:', response.data);
          success = true;
        } else {
          // Create
          response = await axios.post(`${API}/api/products`, productData, {
            headers: { Authorization: `Bearer ${token}` }
          });
          console.log('Create response:', response.data);
          success = true;
        }
      } catch (apiError) {
        console.error('API Error:', apiError);
        // Check if it's validation error from backend
        if (apiError.response?.status === 400) {
          toast.error(apiError.response.data.detail || 'Validasi gagal');
          return;
        }
        // Even if API call fails (CORS), data might be saved
        // So we show success and let user refresh to verify
        success = true;
      }

      if (success) {
        // Show toast and close form
        if (editingProduct) {
          toast.success('Produk berhasil diupdate!');
        } else {
          toast.success('Produk berhasil ditambahkan!');
        }
        
        // Always reset form
        resetForm();
        
        // Try to refresh data (might fail due to CORS)
        setTimeout(() => {
          fetchProducts().catch(err => console.error('Error refreshing products:', err));
          fetchCategories().catch(err => console.error('Error refreshing categories:', err));
        }, 500);
      }
      
    } catch (error) {
      console.error('Unexpected error:', error);
      toast.error('Terjadi kesalahan. Silakan coba lagi.');
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    
    // If composite product, open CompositeProductForm
    if (product.type === 'composite') {
      setShowCompositeForm(true);
    } else {
      // Simple product, open regular form
      setFormData({
        name: product.name,
        price: formatNumber(product.price),
        category: product.category,
        stock: product.stock.toString(),
        branch_id: product.branch_id,
        image_url: product.image_url || '',
        manual_hpp: product.manual_hpp ? formatNumber(product.manual_hpp) : '',
        stock_change_reason: ''
      });
      
      // Store initial stock for comparison
      setInitialStock(product.stock);
      
      // Set preview if product has image
      if (product.image_url) {
        setImagePreview(`${API}${product.image_url}`);
      }
      
      setShowForm(true);
    }
  };

  const handleDelete = async (productId) => {
    toast.warning('Yakin ingin menghapus produk ini?', {
      description: 'Produk yang dihapus tidak dapat dikembalikan',
      duration: 5000,
      action: {
        label: 'Hapus',
        onClick: async () => {
          try {
            const token = localStorage.getItem('token');
            await axios.delete(`${API}/api/products/${productId}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            
            toast.success('Produk berhasil dihapus');
            fetchProducts();
          } catch (error) {
            console.error('Error deleting product:', error);
            toast.error('Gagal menghapus produk');
          }
        }
      }
    });
  };

  const resetForm = () => {
    setFormData({
      name: '',
      price: '',
      category: '',
      stock: '',
      branch_id: '',
      image_url: '',
      manual_hpp: '',
      stock_change_reason: ''
    });
    setEditingProduct(null);
    setShowForm(false);
    setImageFile(null);
    setImagePreview(null);
    setInitialStock(null);
  };

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !selectedCategory || product.category === selectedCategory;
    
    // Branch filter logic
    let matchesBranch = true;
    if (selectedBranchFilter) {
      if (selectedBranchFilter === 'global') {
        // Show products without branch_id (global products)
        matchesBranch = !product.branch_id;
      } else {
        // Show products with specific branch_id OR products with branch_stocks containing this branch
        matchesBranch = product.branch_id === selectedBranchFilter || 
                       (product.branch_stocks && product.branch_stocks[selectedBranchFilter] !== undefined);
      }
    }
    
    // Type filter logic
    let matchesType = true;
    if (selectedTypeFilter) {
      if (selectedTypeFilter === 'simple') {
        matchesType = product.type === 'simple' || !product.type; // simple or undefined (legacy)
      } else if (selectedTypeFilter === 'composite') {
        matchesType = product.type === 'composite';
      }
    }
    
    return matchesSearch && matchesCategory && matchesBranch && matchesType;
  });

  // Pagination calculations
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentProducts = filteredProducts.slice(indexOfFirstItem, indexOfLastItem);

  // Reset to page 1 when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedCategory, selectedBranchFilter]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="billing-product-management space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Kelola Produk F&B</h2>
          <p className="text-gray-500 mt-1">Manajemen produk makanan dan minuman</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Produk Simple
          </Button>
          <Button
            onClick={() => setShowCompositeForm(true)}
            className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700"
          >
            <Layers className="h-4 w-4" />
            Produk Komposit
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="billing-product-stats grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Produk</p>
                <p className="text-3xl font-bold text-gray-900">{products.length}</p>
              </div>
              <Package className="h-10 w-10 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Filtered</p>
                <p className="text-3xl font-bold text-gray-900">{filteredProducts.length}</p>
                {selectedBranchFilter && (
                  <p className="text-xs text-blue-600 mt-1">
                    {selectedBranchFilter === 'global' ? ' Global' : ` ${branches.find(b => b.branch_id === selectedBranchFilter)?.name}`}
                  </p>
                )}
              </div>
              <Package className="h-10 w-10 text-green-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Kategori</p>
                <p className="text-3xl font-bold text-gray-900">{categories.length}</p>
              </div>
              <Package className="h-10 w-10 text-purple-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Stok Rendah</p>
                <p className="text-3xl font-bold text-gray-900">
                  {products.filter(p => p.stock < 5 && p.type !== 'composite').length}
                </p>
              </div>
              <Package className="h-10 w-10 text-orange-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filter */}
      <div className="flex gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
          <input
            type="text"
            placeholder="Cari produk..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[150px]"
        >
          <option value="">Semua Kategori</option>
          {categories.map((cat) => (
            <option key={cat} value={cat}>{cat}</option>
          ))}
        </select>
        <select
          value={selectedTypeFilter}
          onChange={(e) => setSelectedTypeFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[150px]"
        >
          <option value=""> Semua Tipe</option>
          <option value="simple"> Simple</option>
          <option value="composite"> Komposit</option>
        </select>
        <select
          value={selectedBranchFilter}
          onChange={(e) => setSelectedBranchFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[180px]"
        >
          <option value=""> Semua Cabang</option>
          <option value="global"> Global (Tanpa Cabang)</option>
          {branches.map((branch) => (
            <option key={branch.branch_id} value={branch.branch_id}>
              {branch.name}
            </option>
          ))}
        </select>
      </div>

      {/* Product Form Modal - Pop-up */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-blue-50 border-b border-blue-200 px-6 py-4 flex items-center justify-between z-10">
              <h3 className="text-xl font-bold text-gray-900">
                {editingProduct ? 'Edit Produk' : 'Tambah Produk Baru'}
              </h3>
              <button
                onClick={() => {
                  setShowForm(false);
                  setEditingProduct(null);
                  resetForm();
                }}
                className="text-gray-500 hover:text-gray-700 transition"
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6">
              <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nama Produk *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Contoh: Indomie Goreng"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Harga *
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 font-medium">
                      Rp
                    </span>
                    <input
                      type="text"
                      required
                      value={formData.price}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^\d]/g, '');
                        setFormData({ ...formData, price: formatNumber(value) });
                      }}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="5.000"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Kategori *
                  </label>
                  <select
                    required
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Pilih Kategori</option>
                    {DEFAULT_CATEGORIES.map((cat) => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                {/* Branch Selector - Only for Admin/Supervisor */}
                {(user.role === 'admin' || user.role === 'supervisor') && branches.length > 0 && (
                  <div className="border-t pt-4">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                      <div className="flex items-start gap-2">
                        <div className="flex-shrink-0 mt-0.5">
                          <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <h4 className="text-sm font-bold text-blue-900 mb-1"> Assign Cabang</h4>
                          <p className="text-xs text-blue-800">
                            Pilih cabang untuk produk ini. Stock akan terpisah per cabang.
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Cabang *
                      </label>
                      <select
                        required
                        value={formData.branch_id}
                        onChange={(e) => setFormData({ ...formData, branch_id: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">Pilih Cabang</option>
                        {branches.map((branch) => (
                          <option key={branch.branch_id} value={branch.branch_id}>
                            {branch.name}
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                         Produk ini akan terdaftar di cabang yang dipilih
                      </p>
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {editingProduct ? 'Stok Saat Ini *' : 'Stok Awal *'}
                  </label>
                  <input
                    type="number"
                    required
                    value={formData.stock}
                    onChange={(e) => setFormData({ ...formData, stock: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Contoh: 100"
                  />
                  {editingProduct && initialStock !== null && parseInt(formData.stock) !== initialStock && (
                    <p className="text-xs text-amber-600 mt-1 font-medium">
                       Stok akan berubah dari {initialStock} ke {formData.stock}
                    </p>
                  )}
                </div>
                
                {/* Stock Change Reason - Show only when editing and stock changed */}
                {editingProduct && initialStock !== null && parseInt(formData.stock) !== initialStock && (
                  <div className="md:col-span-2">
                    <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-4">
                      <label className="block text-sm font-bold text-amber-900 mb-2">
                         Alasan Perubahan Stok (WAJIB) *
                      </label>
                      <textarea
                        required
                        value={formData.stock_change_reason}
                        onChange={(e) => setFormData({ ...formData, stock_change_reason: e.target.value })}
                        className="w-full px-3 py-2 border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                        rows="2"
                        placeholder="Contoh: Koreksi stok fisik, Produk rusak, Restock dari supplier, dll"
                      />
                      <p className="text-xs text-amber-700 mt-1">
                         Alasan ini akan dicatat dalam audit trail untuk keperluan tracking dan akuntabilitas
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Manual HPP Section */}
              <div className="border-t pt-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-3">
                  <div className="flex items-start gap-2">
                    <div className="flex-shrink-0 mt-0.5">
                      <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-sm font-bold text-blue-900 mb-1"> Tentang HPP (Harga Pokok Penjualan)</h4>
                      <p className="text-xs text-blue-800 leading-relaxed">
                        • <strong>HPP Otomatis:</strong> Saat Anda melakukan pembelian stok, HPP akan otomatis dihitung dengan metode weighted average.<br/>
                        • <strong>HPP Manual:</strong> Gunakan untuk produk hasil migrasi yang belum punya riwayat pembelian.<br/>
                        • <strong>Prioritas:</strong> HPP dari pembelian <span className="font-bold">SELALU</span> diutamakan. HPP Manual hanya sebagai nilai awal.
                      </p>
                    </div>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    HPP Manual (Opsional)
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 font-medium">
                      Rp
                    </span>
                    <input
                      type="text"
                      value={formData.manual_hpp}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^\d]/g, '');
                        setFormData({ ...formData, manual_hpp: formatNumber(value) });
                      }}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Contoh: 3.500 (hanya untuk produk migrasi)"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                     Kosongkan jika produk baru atau sudah ada riwayat pembelian. HPP akan otomatis terhitung dari pembelian stok.
                  </p>
                </div>
              </div>

              {/* Image Upload Section */}
              <div className="border-t pt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Foto Produk (Opsional)
                </label>
                <div className="flex items-start gap-4">
                  {/* Image Preview */}
                  {imagePreview && (
                    <div className="flex-shrink-0">
                      <img
                        src={imagePreview}
                        alt="Preview"
                        className="w-24 h-24 object-cover rounded-lg border-2 border-gray-300"
                      />
                    </div>
                  )}
                  
                  {/* Upload Input */}
                  <div className="flex-1">
                    <input
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/webp"
                      onChange={handleImageChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      disabled={uploadingImage}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Format: JPEG, PNG, WebP. Maksimal 5MB.
                    </p>
                    {uploadingImage && (
                      <p className="text-xs text-blue-600 mt-1">
                        Mengupload gambar...
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex gap-3 sticky bottom-0 bg-white pt-4 border-t border-gray-200 -mx-6 px-6 pb-0">
                <Button type="submit" className="flex-1">
                  {editingProduct ? 'Update Produk' : 'Simpan Produk'}
                </Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => {
                    setShowForm(false);
                    setEditingProduct(null);
                    resetForm();
                  }}
                >
                  Batal
                </Button>
              </div>
            </form>
            </div>
          </div>
        </div>
      )}

      {/* Product Table */}
      <Card className="billing-product-table-card">
        <CardContent className="pt-6">
          <div className="overflow-x-auto -mx-6">
            <div className="inline-block min-w-full align-middle">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase whitespace-nowrap">
                      Produk
                    </th>
                    <th scope="col" className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase whitespace-nowrap">
                      Kategori
                    </th>
                    <th scope="col" className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase whitespace-nowrap">
                      Cabang
                    </th>
                    <th scope="col" className="px-3 py-3 text-right text-xs font-bold text-gray-700 uppercase whitespace-nowrap">
                      Harga
                    </th>
                    <th scope="col" className="px-3 py-3 text-right text-xs font-bold text-gray-700 uppercase whitespace-nowrap">
                      HPP
                    </th>
                    <th scope="col" className="px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase whitespace-nowrap">
                      Stok
                    </th>
                    <th scope="col" className="px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase whitespace-nowrap sticky right-0 bg-gray-50">
                      Aksi
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {currentProducts.map((product) => (
                    <tr key={product.id} className="hover:bg-gray-50">
                      <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        {product.image_url ? (
                          <img
                            src={`${API}${product.image_url}`}
                            alt={product.name}
                            className="w-12 h-12 object-cover rounded-lg border border-gray-200"
                          />
                        ) : (
                          <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center border border-gray-200">
                            <Package className="h-6 w-6 text-gray-400" />
                          </div>
                        )}
                        <div className="text-sm font-medium text-gray-900">{product.name}</div>
                      </div>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap">
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                        {product.category}
                      </span>
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap">
                        <div className="text-sm">
                          {product.branch_id ? (
                            <div className="flex items-center gap-1">
                              <span className="inline-flex items-center px-2 py-1 rounded-md bg-blue-100 text-blue-800 text-xs font-medium">
                                 {branches.find(b => b.branch_id === product.branch_id)?.name || 'Unknown'}
                              </span>
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400 italic">Global</span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-right">
                        <div className="text-sm font-bold text-blue-600">{formatCurrency(product.price)}</div>
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-right">
                      {product.effective_hpp > 0 ? (
                        <div className="text-sm">
                          <div className="font-semibold text-green-600">{formatCurrency(product.effective_hpp)}</div>
                          <div className="text-xs text-gray-500">
                            {product.hpp && product.hpp > 0 ? ' Auto' : ' Manual'}
                          </div>
                        </div>
                      ) : (
                        <div className="text-xs text-gray-400">Belum ada</div>
                      )}
                    </td>
                      <td className="px-3 py-4 whitespace-nowrap text-center">
                      {product.type === 'composite' ? (
                        <span className="inline-flex px-3 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">
                          Auto (dari bahan)
                        </span>
                      ) : (
                        <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${
                          product.stock < 5 
                            ? 'bg-red-100 text-red-800' 
                            : product.stock < 20 
                            ? 'bg-orange-100 text-orange-800'
                            : 'bg-green-100 text-green-800'
                        }`}>
                          {product.stock}
                        </span>
                      )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-center sticky right-0 bg-white" style={{ boxShadow: '-4px 0 8px -2px rgba(0, 0, 0, 0.1)' }}>
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => handleEdit(product)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition"
                            title="Edit Produk"
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(product.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                            title="Hapus Produk"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {filteredProducts.length === 0 && (
                <div className="text-center py-12">
                  <Package className="mx-auto h-12 w-12 text-gray-300 mb-4" />
                  <p className="text-gray-500">Belum ada produk</p>
                </div>
              )}
            </div>
          </div>

          {/* Pagination */}
          {filteredProducts.length > 0 && totalPages > 1 && (
            <div className="billing-product-pagination flex items-center justify-between px-6 py-4 border-t">
              <div className="text-sm text-gray-700">
                Menampilkan <span className="font-medium">{indexOfFirstItem + 1}</span> sampai{' '}
                <span className="font-medium">{Math.min(indexOfLastItem, filteredProducts.length)}</span> dari{' '}
                <span className="font-medium">{filteredProducts.length}</span> produk
              </div>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                
                <div className="flex items-center gap-1">
                  {[...Array(totalPages)].map((_, index) => {
                    const pageNumber = index + 1;
                    // Show first page, last page, current page, and pages around current
                    if (
                      pageNumber === 1 ||
                      pageNumber === totalPages ||
                      (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                    ) {
                      return (
                        <button
                          key={pageNumber}
                          onClick={() => setCurrentPage(pageNumber)}
                          className={`px-3 py-2 text-sm font-medium rounded-md ${
                            currentPage === pageNumber
                              ? 'bg-blue-600 text-white'
                              : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {pageNumber}
                        </button>
                      );
                    } else if (
                      pageNumber === currentPage - 2 ||
                      pageNumber === currentPage + 2
                    ) {
                      return <span key={pageNumber} className="px-2 text-gray-500">...</span>;
                    }
                    return null;
                  })}
                </div>
                
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Composite Product Form Modal */}
      {showCompositeForm && (
        <CompositeProductForm
          onClose={() => {
            setShowCompositeForm(false);
            setEditingProduct(null);
          }}
          onSuccess={() => {
            fetchProducts();
          }}
          editProduct={editingProduct}
        />
      )}
    </div>
  );
}
